import React, { useEffect } from 'react';
import { Platform } from 'react-native';
import { getStateFromPath, NavigationContainer, DefaultTheme, DarkTheme, type Theme } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { useAuth } from '../auth/AuthProvider';
import { Login } from '../features/public/Login';
import { Onboarding } from '../features/public/Onboarding';
import { useBreakpoint } from '../lib/hooks';
import { Icon, Loading, useTheme, type IconName } from '../ui';
import { setCurrentRoute } from './currentRoute';
import { gated, NotFound } from './Gate';
import { isPublicPath, linking } from './linking';
import { MoreScreen } from './MoreScreen';
import { navigationRef } from './nav';
import { isPermitted, SCREENS, screenByName } from './registry';
import { Shell } from './Shell';
import { MORE_TAB, TAB_NAMES, TABS } from './tabs';

const Stack = createNativeStackNavigator();
const noTabBar = () => null;
const tabIcon = (name: IconName) => ({ color, size }: { color: string; size: number }) => <Icon name={name} color={color} size={size - 2} />;
const Tab = createBottomTabNavigator();

/** Web: remember a protected URL opened while signed out and restore it after login. */
let pendingPath: string | null = null;
if (Platform.OS === 'web' && typeof window !== 'undefined') {
  const p = window.location.pathname + window.location.search;
  if (p !== '/' && !isPublicPath(p)) pendingPath = p;
}

function TabsNavigator() {
  const { colors } = useTheme();
  const { isWide } = useBreakpoint();
  const { can } = useAuth();
  const tabs = TABS.filter((t) => {
    const def = screenByName(t.name);
    return def && isPermitted(def, can) && (!t.perms || can(...t.perms));
  });
  return (
    <Tab.Navigator
      backBehavior="history"
      tabBar={isWide ? noTabBar : undefined}
      screenOptions={{
        headerShown: false,
        tabBarActiveTintColor: colors.primary,
        tabBarInactiveTintColor: colors.muted,
        tabBarStyle: { backgroundColor: colors.surface, borderTopColor: colors.border },
        tabBarLabelStyle: { fontSize: 10, fontWeight: '500' },
      }}
    >
      {tabs.map((t) => {
        const def = screenByName(t.name)!;
        return (
          <Tab.Screen
            key={t.name}
            name={t.name}
            component={gated(def)}
            options={{ title: def.title, tabBarLabel: t.label, tabBarButtonTestID: `tab-${t.name}`, tabBarIcon: tabIcon(t.icon) }}
          />
        );
      })}
      <Tab.Screen name={MORE_TAB} component={MoreScreen} options={{ title: 'More', tabBarButtonTestID: 'tab-More', tabBarIcon: tabIcon('menu') }} />
    </Tab.Navigator>
  );
}

function AuthedStack() {
  useEffect(() => {
    if (!pendingPath) return;
    const path = pendingPath;
    pendingPath = null;
    const t = setTimeout(() => {
      const state = getStateFromPath(path, linking.config);
      if (state && navigationRef.isReady()) navigationRef.resetRoot(state as Parameters<typeof navigationRef.resetRoot>[0]);
    }, 0);
    return () => clearTimeout(t);
  }, []);
  return (
    <Shell>
      <Stack.Navigator screenOptions={{ headerShown: false, animation: Platform.OS === 'web' ? 'none' : 'default' }}>
        <Stack.Screen name="Tabs" component={TabsNavigator} options={{ title: 'Home' }} />
        {SCREENS.filter((s) => !TAB_NAMES.has(s.name)).map((s) => (
          <Stack.Screen key={s.name} name={s.name} component={gated(s)} options={{ title: s.title }} />
        ))}
        <Stack.Screen name="Onboarding" component={Onboarding} options={{ title: 'Onboarding' }} />
        <Stack.Screen name="NotFound" component={NotFound} options={{ title: 'Not found' }} />
      </Stack.Navigator>
    </Shell>
  );
}

function PublicStack() {
  return (
    <Stack.Navigator screenOptions={{ headerShown: false }}>
      <Stack.Screen name="Login" component={Login} options={{ title: 'Sign in' }} />
      <Stack.Screen name="Onboarding" component={Onboarding} options={{ title: 'Complete your onboarding' }} />
    </Stack.Navigator>
  );
}

export function RootNavigator() {
  const { isAuthenticated, loading } = useAuth();
  const { colors, dark } = useTheme();
  const navTheme: Theme = {
    ...(dark ? DarkTheme : DefaultTheme),
    colors: { ...(dark ? DarkTheme : DefaultTheme).colors, primary: colors.primary, background: colors.bg, card: colors.surface, text: colors.text, border: colors.border },
  };
  if (loading) return <Loading label="Signing you in…" />;
  return (
    <NavigationContainer
      ref={navigationRef}
      linking={linking}
      theme={navTheme}
      fallback={<Loading />}
      documentTitle={{ formatter: (options, route) => `${options?.title ?? route?.name ?? 'Helios HRMS'} · Helios HRMS` }}
      onReady={() => setCurrentRoute(navigationRef.getCurrentRoute()?.name)}
      onStateChange={() => setCurrentRoute(navigationRef.getCurrentRoute()?.name)}
    >
      {isAuthenticated ? <AuthedStack /> : <PublicStack />}
    </NavigationContainer>
  );
}
